import React, { useEffect, useState } from "react";
import { WhatsappLogo } from "@phosphor-icons/react";
import { STICKY, getWhatsAppHref } from "../../data/startPageContent";
import { trackPixelEvent } from "../../hooks/useFacebookPixel";
import "./StartStickyBar.css";

const FORM_IDS = ["start-form", "start-form-bottom"];

/**
 * Mobile sticky CTA bar (plan §4.11). Hidden while any lead form is in view.
 * CK-1062 fallback: always visible.
 */
export default function StartStickyBar() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || navigator.webdriver) return undefined;
    try {
      if (!("IntersectionObserver" in window)) {
        setVisible(true);
        return undefined;
      }
      const inView = new Map();
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => inView.set(e.target, e.isIntersecting));
          setVisible(![...inView.values()].some(Boolean));
        },
        { threshold: 0.15 }
      );
      let observed = 0;
      FORM_IDS.forEach((id) => {
        const el = document.getElementById(id);
        if (el) {
          observer.observe(el);
          observed += 1;
        }
      });
      if (observed === 0) setVisible(true);

      // Forms re-mount on success (same id) → re-observe on DOM changes.
      const mo = new MutationObserver(() => {
        FORM_IDS.forEach((id) => {
          const el = document.getElementById(id);
          if (el && !inView.has(el)) observer.observe(el);
        });
      });
      mo.observe(document.body, { childList: true, subtree: true });

      return () => {
        observer.disconnect();
        mo.disconnect();
      };
    } catch (error) {
      console.error("[CK-1062] StartStickyBar observer failed", {
        errorCode: "CK-1062",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      setVisible(true);
      return undefined;
    }
  }, []);

  const scrollToForm = (e) => {
    e.preventDefault();
    const bottom = document.getElementById("start-form-bottom");
    const hero = document.getElementById("start-form");
    const mid = window.scrollY + window.innerHeight / 2;
    const target =
      bottom && hero && Math.abs(bottom.getBoundingClientRect().top + window.scrollY - mid) <
        Math.abs(hero.getBoundingClientRect().top + window.scrollY - mid)
        ? bottom
        : hero || bottom;
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
    target?.querySelector("input")?.focus({ preventScroll: true });
  };

  return (
    <div className={`st-sticky${visible ? " st-sticky--visible" : ""}`} aria-hidden={!visible}>
      <a href="#start-form" className="st-btn st-btn--primary st-sticky-primary" onClick={scrollToForm} tabIndex={visible ? 0 : -1}>
        {STICKY.primary}
      </a>
      <a
        href={getWhatsAppHref()}
        className="st-btn st-btn--ghost st-sticky-wa"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={STICKY.waAria}
        tabIndex={visible ? 0 : -1}
        onClick={() => trackPixelEvent("Contact", { content_name: "get_started_page" })}
      >
        <WhatsappLogo size={22} weight="fill" />
      </a>
    </div>
  );
}
